import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { getChapter, getBook } from '../../services/api';
import './Reader.css';

interface ChapterData {
  id: number;
  book_id: number;
  title: string;
  content: string;
  order_num: number;
}

interface BookData {
  id: number;
  title: string;
  chapters: { id: number; title: string; order_num: number }[];
}

const Reader: React.FC = () => {
  const { bookId, chapterId } = useParams<{ bookId: string; chapterId: string }>();
  const [chapter, setChapter] = useState<ChapterData | null>(null);
  const [book, setBook] = useState<BookData | null>(null);
  const [loading, setLoading] = useState(true);
  const [fontSize, setFontSize] = useState(18);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [theme, setTheme] = useState<'dark' | 'sepia' | 'light'>('dark');
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (bookId && chapterId) {
      setLoading(true);
      Promise.all([
        getChapter(parseInt(bookId), parseInt(chapterId)),
        getBook(parseInt(bookId)),
      ])
        .then(([chapterRes, bookRes]) => {
          setChapter(chapterRes.data);
          setBook(bookRes.data);
        })
        .catch(console.error)
        .finally(() => setLoading(false));
    }
  }, [bookId, chapterId]);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(docHeight > 0 ? (scrollTop / docHeight) * 100 : 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const currentIndex = book?.chapters?.findIndex((ch) => ch.id === parseInt(chapterId || '0')) ?? -1;
  const prevChapter = currentIndex > 0 ? book?.chapters[currentIndex - 1] : null;
  const nextChapter = book?.chapters && currentIndex < book.chapters.length - 1 ? book.chapters[currentIndex + 1] : null;

  const themeClasses: Record<string, string> = {
    dark: 'reader-dark',
    sepia: 'reader-sepia',
    light: 'reader-light',
  };

  if (loading) {
    return (
      <div className="loading-container" style={{ paddingTop: 120 }}>
        <div className="spinner" />
      </div>
    );
  }

  if (!chapter || !book) {
    return (
      <div className="loading-container" style={{ paddingTop: 120 }}>
        <p>Chapter not found</p>
      </div>
    );
  }

  return (
    <div className={`reader-page ${themeClasses[theme]}`}>
      {/* Progress bar */}
      <div className="reader-progress">
        <div className="reader-progress-bar" style={{ width: `${progress}%` }} />
      </div>

      {/* Top toolbar */}
      <motion.div
        className="reader-toolbar"
        initial={{ y: -60 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <Link to={`/books/${book.id}`} className="reader-back">
          &larr; {book.title}
        </Link>
        <div className="reader-controls">
          <button
            className="reader-ctrl-btn"
            onClick={() => setFontSize(Math.max(14, fontSize - 2))}
            title="Decrease font size"
          >
            A-
          </button>
          <span className="font-size-display">{fontSize}px</span>
          <button
            className="reader-ctrl-btn"
            onClick={() => setFontSize(Math.min(28, fontSize + 2))}
            title="Increase font size"
          >
            A+
          </button>
          <div className="theme-switcher">
            <button
              className={`theme-btn theme-dark ${theme === 'dark' ? 'active' : ''}`}
              onClick={() => setTheme('dark')}
              title="Dark theme"
            />
            <button
              className={`theme-btn theme-sepia ${theme === 'sepia' ? 'active' : ''}`}
              onClick={() => setTheme('sepia')}
              title="Sepia theme"
            />
            <button
              className={`theme-btn theme-light-btn ${theme === 'light' ? 'active' : ''}`}
              onClick={() => setTheme('light')}
              title="Light theme"
            />
          </div>
          <button
            className="reader-ctrl-btn toc-btn"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            &#9776;
          </button>
        </div>
      </motion.div>

      {/* Sidebar / Table of Contents */}
      <AnimatePresence>
        {sidebarOpen && (
          <>
            <motion.div
              className="reader-sidebar-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSidebarOpen(false)}
            />
            <motion.div
              className="reader-sidebar"
              initial={{ x: 320 }}
              animate={{ x: 0 }}
              exit={{ x: 320 }}
              transition={{ type: 'spring', damping: 25 }}
            >
              <div className="sidebar-header">
                <h3>Table of Contents</h3>
                <button onClick={() => setSidebarOpen(false)}>&times;</button>
              </div>
              <div className="sidebar-chapters">
                {book.chapters.map((ch) => (
                  <Link
                    key={ch.id}
                    to={`/read/book/${book.id}/chapter/${ch.id}`}
                    className={`sidebar-chapter ${ch.id === chapter.id ? 'active' : ''}`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <span className="sidebar-ch-num">{String(ch.order_num).padStart(2, '0')}</span>
                    <span>{ch.title}</span>
                  </Link>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Reading content */}
      <motion.article
        className="reader-content"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        style={{ fontSize: `${fontSize}px` }}
      >
        <div className="reader-chapter-header">
          <span className="reader-chapter-num">Chapter {chapter.order_num}</span>
          <h1>{chapter.title}</h1>
          <div className="reader-divider">
            <span>&#8734;</span>
          </div>
        </div>

        <div className="reader-text">
          {chapter.content.split('\n\n').map((paragraph, index) => (
            <p key={index}>{paragraph}</p>
          ))}
        </div>

        <div className="reader-end-mark">&#8734;</div>
      </motion.article>

      {/* Chapter navigation */}
      <div className="reader-nav">
        <div className="reader-nav-inner">
          {prevChapter ? (
            <Link
              to={`/read/book/${book.id}/chapter/${prevChapter.id}`}
              className="reader-nav-btn prev"
            >
              <span className="nav-direction">&larr; Previous</span>
              <span className="nav-chapter-title">{prevChapter.title}</span>
            </Link>
          ) : (
            <div />
          )}
          {nextChapter ? (
            <Link
              to={`/read/book/${book.id}/chapter/${nextChapter.id}`}
              className="reader-nav-btn next"
            >
              <span className="nav-direction">Next &rarr;</span>
              <span className="nav-chapter-title">{nextChapter.title}</span>
            </Link>
          ) : (
            <Link to={`/books/${book.id}`} className="reader-nav-btn next">
              <span className="nav-direction">Finished!</span>
              <span className="nav-chapter-title">Back to book</span>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reader;
